import React, { useState, useEffect, useLayoutEffect, lazy, Suspense, useMemo, useCallback, useRef } from "react";
import { isEmpty } from "lodash";
import { connect, useDispatch, useSelector } from "react-redux";
import { Collapse } from "@mui/material";
import mixpanel from "mixpanel-browser";
import InputRow from "components/Input/Row";
import Table from "components/Table";
import Field from "components/Field";
import Input from "components/Input";
import Select from "components/Input/Select";
import Button from "components/Button";
import LoadingSection from "components/Loading/LoadingSection";
import LoadingDots from "components/LoadingDots";
import Dialog, { CloseDialog } from "components/Dialog";

import CsvExport from "features/Model/Review/UtiltyData/CsvExport";
import XmlUpload from "features/Model/Review/UtiltyData/XmlUpload";
import DemonstratedLoadSettingsPanel, {
    DemonstratedLoadResults,
    ProposedLoadsSection,
} from "features/Model/Review/UtiltyData/DemonstratedLoadPanel";
import { change as changeAction } from "store/root/actions";
import HelpIcon from "assets/images/icons/JSX/Help";
import ArrowDown from "assets/images/icons/JSX/arrow-down";

import {
    getMonth,
    getYear,
    months,
    monthList,
    days,
    dayIndex,
    rows,
    calculateBaseLoad,
    getMeanAndMedianEnergyConsumption,
    createHistogramPMF,
    combineUtilitySimulatedData,
} from "utils/utilityData/utilityData";
import {
    computeDemonstratedLoad,
    DEFAULT_DEMONSTRATED_LOAD_SETTINGS,
    createProposedLoadKey,
    MAX_PROPOSED_LOADS,
} from "utils/utilityData/demonstratedLoad";
import { useStateSelector } from "utils/field";
import clientF280Weather from "utils/weather/clientF280Weather.json";
import { deleteModelGreenButtonData, getDetailedHourlyResults, getGreenButtonWeatherData } from "utils/results/api";
import { getPackageRunId } from "utils/results/runIdentity";
import { resolveStoredCaseKey } from "utils/referenceHouse";

import globalStyles from "components/globalStyles.module.scss";
import classes from "features/Model/Review/UtiltyData/style.module.scss";
import {
    selectModelDetails,
    selectModelId,
    selectHeatingCooling,
    selectDomesticHotWater,
} from "features/Model/_ducks/selectors";
import { selectUserPermissions } from "store/users/selectors";
import { actions } from "features/Model/_ducks";
import { actions as resultsActions } from "features/Model/Review/Results/_ducks";

const {
    changeUtilityDataPackage,
    fetchGreenButtonData,
    clearGreenButtonData,
    toggleGreenButtonWeatherAnalysisLoading,
    toggleGreenButtonBaseLoadCalculationLoading,
} = actions;
const { getResults } = resultsActions;

/** Session-only hourly / binned data — lives on model slice, not modelData (so Save dirty-state stays correct). */
const SESSION_GREEN_BUTTON = "model.greenButtonData";
const EMPTY_PROPOSED_LOADS_OBJECT = {};
const DEMONSTRATED_LOAD_KEY = "demonstratedLoad";

const buildDemonstratedLoadPersistKey = (result, proposedLoadKeys = []) =>
    JSON.stringify({
        peakDemandAmps: result?.peakDemandAmps,
        headroomAmps: result?.headroomAmps,
        peakHourStart: result?.peakHourStart,
        totalProposedAmps: result?.totalProposedAmps,
        proposedLoadKeys: [...proposedLoadKeys].sort(),
        warnings: result?.warnings,
    });

const LazyLoadedPlots = lazy(() => import("features/Model/Review/UtiltyData/GreenButtonDataPlot"));

const TableInput = (selectedFuelType, selectedTableType, cellType, ind, accessor, firstMonth, firstYear, change) => {
    return cellType === "year" ? (
        <Field
            component={Input}
            type="number"
            name={`${accessor}.${selectedTableType}.${selectedFuelType}.row${ind}.${cellType}`}
            change={change}
            setValue={getYear(ind, firstMonth, firstYear)}
        />
    ) : cellType === "fuelUse" ? (
        <Field
            component={Input}
            type="number"
            name={`${accessor}.${selectedTableType}.${selectedFuelType}.row${ind}.${cellType}`}
            change={change}
            normalize={(value) => {
                // Optionally, you can add extra normalization logic here
                // This function receives every keystroke and can strip extra minus signs.
                const empty = /^$/;
                const reg = /[\d]/;
                // return empty.test(value) ? 0 : reg.test(value) ? value : previousValue;
                // console.log("value", (value = value === "+" || value === "-" ? value : parseFloat(value)));
                return (value = value === "+" || value === "-" ? value : parseFloat(value));
                // if (!value) return value;
                // // Allow a single minus only at the beginning.
                // if (value[0] === "-") {
                //     // Remove minus signs that appear after the first character.
                //     return "-" + value.slice(1).replace(/-/g, "");
                // }
                // return value.replace(/-/g, "");
            }}
        />
    ) : cellType === "fuelCost" ? (
        <Field
            component={Input}
            type="number"
            name={`${accessor}.${selectedTableType}.${selectedFuelType}.row${ind}.${cellType}`}
            change={change}
        />
    ) : cellType === "notes" ? (
        <Field
            component={Input}
            type="text"
            name={`${accessor}.${selectedTableType}.${selectedFuelType}.row${ind}.${cellType}`}
            change={change}
        />
    ) : cellType === "month" ? (
        <Field
            component={Select}
            name={`${accessor}.${selectedTableType}.${selectedFuelType}.row${ind}.${cellType}`}
            change={change}
            options={months}
            disabled={ind !== 0}
            onChange={(month) => {
                rows.map((row, rowInd) => {
                    change(`${accessor}.${selectedTableType}.${selectedFuelType}.row${rowInd}.billingStartDate`, 1);
                    if (monthList.indexOf(month) + rowInd > 11) {
                        change(
                            `${accessor}.${selectedTableType}.${selectedFuelType}.row${rowInd}.${cellType}`,
                            `${monthList[monthList.indexOf(month) + rowInd - 12]}`
                        );
                    } else {
                        change(
                            `${accessor}.${selectedTableType}.${selectedFuelType}.row${rowInd}.${cellType}`,
                            monthList[monthList.indexOf(month) + rowInd]
                        );
                    }
                });
            }}
        />
    ) : cellType === "billingStartDate" ? (
        <Field
            component={Select}
            name={`${accessor}.${selectedTableType}.${selectedFuelType}.row${ind}.${cellType}`}
            change={change}
            search
            options={days[getMonth(ind, firstMonth)]}
            emptyMessage="Select a month."
            onChange={(day) => {
                rows.map((row, rowInd) => {
                    const rowMonth = getMonth(rowInd, firstMonth);
                    const isLastDay = day > dayIndex[rowMonth];
                    change(
                        `${accessor}.${selectedTableType}.${selectedFuelType}.row${rowInd}.${cellType}`,
                        isLastDay ? dayIndex[rowMonth] : day
                    );
                });
            }}
        />
    ) : (
        <></>
    );
};

const fuelTypeLabels = {
    electricity: { label: "Electricity", unit: "kWh" },
    naturalGas: { label: "Natural Gas", unit: "m3" },
    oil: { label: "Oil", unit: "L" },
    propane: { label: "Propane", unit: "L" },
    woodPellets: { label: "Wood (Pellet)", unit: "kg" },
    woodCord: { label: "Wood (Cord)", unit: "cord" },
};

const UtilityData = ({ accessor, change, modelSave, changeUtilityDataPackage }) => {
    const dispatch = useDispatch();

    const [selectedTableType, setSelectedTableType] = useState("preUpgrade");
    const [selectedFuelType, setSelectedFuelType] = useState("electricity");
    const [selectedPlotType, setSelectedPlotType] = useState("scatterPlot");
    const [selectedDomain, setDomainSelection] = useState("timeDomain");

    const [exporting, toggleExporting] = useState(false);
    const [exportingCsv, toggleCsvExporting] = useState(false);
    const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
    const [step1Open, setStep1Open] = useState(true);
    const [step2Open, setStep2Open] = useState(false);
    const [step3Open, setStep3Open] = useState(false);
    const [demonstratedLoadYAxis, setDemonstratedLoadYAxis] = useState("kwh");
    const demonstratedLoadPersistKeyRef = useRef("");
    const migratedLegacyDemonstratedLoadRef = useRef(false);

    const [csvErrorMsg, toggleErrorMsg] = useState("");
    const [weatherAnalysisError, setWeatherAnalysisError] = useState("");
    const [clearingGreenButton, setClearingGreenButton] = useState(false);
    const [xmlUploadResetKey, setXmlUploadResetKey] = useState(0);

    const uploadIsLoading = useStateSelector("model.greenButtonDataLoading") ?? false;
    const greenButtonFetchError = useStateSelector("model.greenButtonFetchError") ?? null;
    const loadingTemperatureData = useStateSelector("model.greenButtonWeatherAnalysisLoading") ?? false;
    const loadingBaseLoadCalculation = useStateSelector("model.greenButtonBaseLoadCalculationLoading") ?? false;
    const { VOLTA_DEV = false, GREEN_BUTTON_TESTING = false } = useSelector(selectUserPermissions);

    const GREEN_BUTTON_ENABLED = VOLTA_DEV || GREEN_BUTTON_TESTING;

    const utilityDataState = useStateSelector(`${accessor}`) || {};
    const { selectedPackage = "base", hasBaseLoadCalculation = false } = utilityDataState;

    const { packageResults: packageResultsData = {}, selectedPackage: resultsSelectedPackage = "base" } =
        useStateSelector("results") || {};
    const upgradesState = useStateSelector("upgrades") || {};
    const { packages = {} } = upgradesState;

    const resultsColumn = packageResultsData?.[resultsSelectedPackage] || {};
    const resultsCaseKey = resolveStoredCaseKey({
        resultsData: resultsColumn.resultsData || {},
        packageId: resultsSelectedPackage,
        packageType: packages?.[resultsSelectedPackage]?.packageType,
        resultsRole: resultsColumn.metadata?.resultsRole,
        operatingConditions: packages?.[resultsSelectedPackage]?.ui?.operatingConditions,
    });
    const h2kResultsBaseloads =
        packageResultsData?.[resultsSelectedPackage]?.resultsData?.[resultsCaseKey]?.resultsSummary?.endUseChart
            ?.baseloads || 0; // GJ/year
    const h2kResultsVentilation =
        packageResultsData?.[resultsSelectedPackage]?.resultsData?.[resultsCaseKey]?.resultsSummary?.endUseChart
            ?.ventilation || 0; // GJ/year
    const h2kResultsVentilation_kWh_day = (h2kResultsVentilation * 277.78) / 365;

    const modelDetails = useSelector(selectModelDetails) || {};
    const modelId = useSelector(selectModelId) || "";
    const heatingCooling = useSelector(selectHeatingCooling) || {};
    const domesticHotWater = useSelector(selectDomesticHotWater) || {};

    const sessionGreenButton = useStateSelector(SESSION_GREEN_BUTTON) || {};
    const { hourlyGreenButtonData = [], combinedUtilityHeatPumpData: binnedUtilityData = [] } = sessionGreenButton;

    const persistedGreenButton = useStateSelector("model.modelData.utilityData.greenButtonData") || {};
    const {
        sourceFileName: hourlyDataSourceFileName = "",
        greenButtonDocumentIds = [],
        hasTemperatureData = false,
        stationCounts = {},
    } = persistedGreenButton;

    const legacyDemonstratedLoad = useStateSelector(`${accessor}.greenButtonData.demonstratedLoad`) || {};
    const demonstratedLoadFromStore = useStateSelector(`${accessor}.${DEMONSTRATED_LOAD_KEY}`) || {};

    const demonstratedLoad = useMemo(() => {
        if (Object.keys(demonstratedLoadFromStore).length > 0) {
            return demonstratedLoadFromStore;
        }
        return legacyDemonstratedLoad;
    }, [demonstratedLoadFromStore, legacyDemonstratedLoad]);

    const proposedLoads = demonstratedLoad.proposedLoads ?? EMPTY_PROPOSED_LOADS_OBJECT;
    const demonstratedLoadSettings = demonstratedLoad.settings;

    useEffect(() => {
        demonstratedLoadPersistKeyRef.current = "";
        migratedLegacyDemonstratedLoadRef.current = false;
    }, [modelId]);

    useEffect(() => {
        if (migratedLegacyDemonstratedLoadRef.current || !modelId) return;
        if (Object.keys(demonstratedLoadFromStore).length > 0) {
            migratedLegacyDemonstratedLoadRef.current = true;
            return;
        }
        if (Object.keys(legacyDemonstratedLoad).length === 0) return;
        migratedLegacyDemonstratedLoadRef.current = true;
        dispatch(changeAction(`${accessor}.${DEMONSTRATED_LOAD_KEY}`, legacyDemonstratedLoad));
    }, [modelId, demonstratedLoadFromStore, legacyDemonstratedLoad, dispatch, accessor]);

    const setProposedLoadsInStore = useCallback(
        (nextProposedLoads) => {
            dispatch(changeAction(`${accessor}.${DEMONSTRATED_LOAD_KEY}.proposedLoads`, nextProposedLoads));
        },
        [dispatch, accessor]
    );

    const demonstratedLoadResult = useMemo(
        () => computeDemonstratedLoad(hourlyGreenButtonData, demonstratedLoadSettings, proposedLoads),
        [hourlyGreenButtonData, demonstratedLoadSettings, proposedLoads]
    );

    const addProposedLoad = useCallback(
        (preset = null) => {
            if (Object.keys(proposedLoads).length >= MAX_PROPOSED_LOADS) return;
            const key = createProposedLoadKey(Object.keys(proposedLoads).length);
            setProposedLoadsInStore({
                ...proposedLoads,
                [key]: {
                    label: preset?.label || "",
                    amps: preset?.amps ?? 0,
                },
            });
        },
        [proposedLoads, setProposedLoadsInStore]
    );

    const updateProposedLoad = useCallback(
        (key, field, value) => {
            setProposedLoadsInStore({
                ...proposedLoads,
                [key]: {
                    ...proposedLoads[key],
                    [field]: value,
                },
            });
        },
        [proposedLoads, setProposedLoadsInStore]
    );

    const removeProposedLoad = useCallback(
        (key) => {
            const nextProposedLoads = { ...proposedLoads };
            delete nextProposedLoads[key];
            setProposedLoadsInStore(nextProposedLoads);
        },
        [proposedLoads, setProposedLoadsInStore]
    );

    const updateDemonstratedLoadSettings = useCallback(
        (field, value) => {
            dispatch(
                changeAction(`${accessor}.${DEMONSTRATED_LOAD_KEY}.settings`, {
                    ...DEFAULT_DEMONSTRATED_LOAD_SETTINGS,
                    ...(demonstratedLoadSettings || {}),
                    [field]: value,
                })
            );
        },
        [dispatch, accessor, demonstratedLoadSettings]
    );

    const demonstratedLoadChart = useMemo(
        () => ({
            showOverlays: selectedDomain === "timeDomain",
            yAxisMode: demonstratedLoadYAxis,
            lastComputed: demonstratedLoadResult,
        }),
        [selectedDomain, demonstratedLoadYAxis, demonstratedLoadResult]
    );

    useEffect(() => {
        if (!demonstratedLoadResult?.peakDemandAmps) return;
        const proposedLoadKeys = Object.keys(proposedLoads);
        const persistKey = buildDemonstratedLoadPersistKey(demonstratedLoadResult, proposedLoadKeys);
        if (persistKey === demonstratedLoadPersistKeyRef.current) return;

        const storedLastComputed = demonstratedLoad.lastComputed;
        if (storedLastComputed?.peakDemandAmps != null) {
            const storedKey = buildDemonstratedLoadPersistKey(storedLastComputed, proposedLoadKeys);
            if (persistKey === storedKey) {
                demonstratedLoadPersistKeyRef.current = persistKey;
                return;
            }
        }

        demonstratedLoadPersistKeyRef.current = persistKey;
        dispatch(
            changeAction(`${accessor}.${DEMONSTRATED_LOAD_KEY}.lastComputed`, {
                ...demonstratedLoadResult,
                computedAt: Date.now(),
            })
        );
    }, [demonstratedLoadResult, proposedLoads, demonstratedLoad, dispatch, accessor]);

    const f280WeatherLocationId = useStateSelector("model.modelData.location.weather.f280.location.id");

    const f280WeatherSearchDisplay = useMemo(() => {
        const id = f280WeatherLocationId;
        if (id == null || id === "") {
            return {
                line: "No F280 weather location is set. Choose CSA F280-12 design conditions under General → Location.",
            };
        }
        const row = clientF280Weather[String(id)];
        if (!row) {
            return { line: `Weather location id ${id} is not recognized in the F280 weather list.` };
        }
        return { line: `Weather data will be matched using F280 design conditions: ${row.City} (${row.Region})` };
    }, [f280WeatherLocationId]);

    const { lastCliRun: { cliId = {}, datetime: dateRun = "" } = {}, simulationRunning = false } = modelDetails;

    const currentCliId = getPackageRunId(cliId, selectedPackage);
    const selectedColumn = packageResultsData?.[selectedPackage] || {};
    const caseKey = resolveStoredCaseKey({
        resultsData: selectedColumn.resultsData || {},
        packageId: selectedPackage,
        packageType: packages?.[selectedPackage]?.packageType,
        resultsRole: selectedColumn.metadata?.resultsRole,
        operatingConditions: packages?.[selectedPackage]?.ui?.operatingConditions,
    });

    const { fuelTypeLabel = "", fuelTypeUnit = "" } = fuelTypeLabels[selectedFuelType] || {};

    const tableData = utilityDataState?.[selectedTableType]?.[selectedFuelType]?.row0 || {};
    const firstMonth = tableData.month || "";
    const firstYear = tableData.year || "";

    const { cooling: { system: coolingSystems = {} } = {}, heating: { system: heatingSystems = {} } = {} } =
        heatingCooling;

    const heatingSystemType = Object.keys(heatingSystems)[0] || "";
    const heatingEnergySourceId = heatingSystems[heatingSystemType]?.energySource?.id;

    const { primarySystem: primaryHotWaterSystem, secondarySystem: secondaryHotWaterSystem } = domesticHotWater;

    const hasElectricHeating =
        heatingSystemType === "baseboards" || (heatingSystemType === "furnace" && heatingEnergySourceId === 0);
    const hasElectricCooling = !isEmpty(coolingSystems); // all cooling systems are electric unless none are selected
    const hasElectricHotWater =
        primaryHotWaterSystem?.energySource?.id === 0 || secondaryHotWaterSystem?.energySource?.id === 0;

    // Current base load values from model (kWh/day)
    const elecApplConsumption = useStateSelector(`model.modelData.baseLoads.summary.elecApplConsumption`) || 0;
    const lightingConsumption = useStateSelector(`model.modelData.baseLoads.summary.lightingConsumption`) || 0;
    const exteriorUseConsumption = useStateSelector(`model.modelData.baseLoads.summary.exteriorUseConsumption`) || 0;
    const otherElecConsumption = useStateSelector(`model.modelData.baseLoads.summary.otherElecConsumption`) || 0;

    const upgradeHeatPumpOpts = useMemo(() => {
        return Object.keys(packages)
            .filter((pkgKey) => {
                if (pkgKey === "base") return false;

                const pkg = packages[pkgKey];
                const upgrades = pkg.upgrades || {};

                // Check if any upgrade in this package includes a heat pump system
                return Object.values(upgrades).some((upgrade) => {
                    const upgradedSystem = Object.keys(upgrade?.fields?.cooling?.system || {});
                    const heatPumpTypes = ["airHeatPump", "groundHeatPump", "waterHeatPump"];
                    return heatPumpTypes.includes(upgradedSystem?.[0] || "None");
                });
            })
            .map((pkgKey) => {
                const pkg = packages[pkgKey];
                return {
                    value: pkgKey,
                    label: pkg.name,
                };
            });
    }, [packages]);

    const handleGreenButtonUploadStart = useCallback(() => {
        setDomainSelection("timeDomain");
        setSelectedPlotType("scatterPlot");
    }, []);

    useEffect(() => {
        // Auto-switch from box plot to scatter plot if temperature data is not available
        if (selectedPlotType === "boxPlot" && !hasTemperatureData) {
            setSelectedPlotType("scatterPlot");
        }
    }, [selectedPlotType, hasTemperatureData]);

    useEffect(() => {
        if (selectedDomain !== "timeDomain" && demonstratedLoadYAxis === "amps") {
            setDemonstratedLoadYAxis("kwh");
        }
    }, [selectedDomain, demonstratedLoadYAxis]);

    // Auto-open next step when previous step completes
    useEffect(() => {
        if (!isEmpty(hourlyGreenButtonData) && !uploadIsLoading) {
            setStep2Open(true);
        }
    }, [hourlyGreenButtonData, uploadIsLoading]);

    useEffect(() => {
        if (hasTemperatureData && !loadingTemperatureData) {
            setStep3Open(true);
        }
    }, [hasTemperatureData, loadingTemperatureData]);

    // Fetch results data if not already loaded and we have CLI IDs
    useEffect(() => {
        const hasResultsData = packageResultsData && Object.keys(packageResultsData).length > 0;
        const hasCliIds = cliId && Object.keys(cliId).length > 0;

        if (!hasResultsData && hasCliIds && modelId) {
            const cliIds = Object.values(cliId)
                .map((cli) => cli.runId)
                .filter((id) => id);
            if (cliIds.length > 0) {
                dispatch(getResults({ cliIds, modelId }));
            }
        }
    }, [cliId, modelId, packageResultsData, dispatch]);

    const hasHourlyGreenButtonData = hourlyGreenButtonData.length > 0;
    /** Document IDs saved but session hourly not yet merged (fetch pending or between paint and Redux commit). */
    const isGreenButtonChartHydrating = useMemo(
        () => !isEmpty(greenButtonDocumentIds) && !hasHourlyGreenButtonData && !greenButtonFetchError,
        [greenButtonDocumentIds, hasHourlyGreenButtonData, greenButtonFetchError]
    );
    const greenButtonDocumentIdsKey = useMemo(
        () => (Array.isArray(greenButtonDocumentIds) ? greenButtonDocumentIds.join(",") : ""),
        [greenButtonDocumentIds]
    );

    useLayoutEffect(() => {
        if (!modelId) return;
        if (uploadIsLoading) return;
        if (hasHourlyGreenButtonData) return;
        if (!greenButtonDocumentIdsKey) return;
        dispatch(fetchGreenButtonData({ modelId }));
    }, [modelId, uploadIsLoading, hasHourlyGreenButtonData, greenButtonDocumentIdsKey, dispatch]);

    const handleCalculateBaseLoad = useCallback(async () => {
        try {
            await modelSave();
            dispatch(toggleGreenButtonBaseLoadCalculationLoading(true));

            if (hourlyGreenButtonData.length > 0) {
                // Base Load Calibration
                const { avgBaseLoad, baseLoadValuesPerBin } = calculateBaseLoad(hourlyGreenButtonData); // in kWh
                const { mean, median } = getMeanAndMedianEnergyConsumption(hourlyGreenButtonData, 14, 18); // to reflect the avg. temperature-independent consumption of the building

                // This value could then be used to calculate the daily consumption (hourly average x 24) of the building which can
                // be used as an input in HOT2000 (see base loads tab) to calibrate the model's base load consumption to.
                let averageDailyConsumption_kWh_day = parseFloat((avgBaseLoad * 24).toFixed(2)); // calculate average daily consumption for HOT2000 input (in kWh/day)
                averageDailyConsumption_kWh_day = Math.max(
                    0,
                    averageDailyConsumption_kWh_day - h2kResultsVentilation_kWh_day
                ); // subtracting simulated ventilation from daily consumption (in kWh/day)
                const averageAnnualConsumption_GJ_year = parseFloat(
                    (averageDailyConsumption_kWh_day * 365 * 0.0036).toFixed(2)
                ); // kWh/day -> GJ/year

                // Calculate the base load PMF centered around 16C at a 5C wide temperature bin
                const baseLoadPMF = createHistogramPMF(hourlyGreenButtonData, {
                    tempMin: 13.5,
                    tempMax: 18.5,
                    binWidth: 0.5,
                    minValue: 0,
                    maxValue: 24,
                }); // these are "likely" base loads

                /* COMMENTED OUT - Heat Pump Data Integration (for future use)
                // Step 1: Heat pump assessment
                // Step 1.1: Non-Electric Base Case
                const { data: { hourlyResults = {} } = {} } = await getDetailedHourlyResults({
                    modelId,
                    cliId: currentCliId,
                    resultsCase: caseKey,
                    upgradePackageId: selectedPackage,
                });

                if (!isEmpty(hourlyResults)) {
                    const {
                        hourlyHeatingkWh = [], // heating load
                        hourlyCoolingkWh = [], // cooling load
                        dbTemperature = [], //Ambient Temperature
                    } = hourlyResults;

                    const hourlyHeatPumpConsumption = hourlyHeatingkWh.map((heating, index) => {
                        const cooling = hourlyCoolingkWh[index] || 0;
                        return heating + cooling; // combined heat pump load per hour in kWh
                    });

                    const formattedHeatPumpData = hourlyHeatPumpConsumption
                        .map((value, index) => {
                            const tempData = dbTemperature?.[index];
                            return {
                                value,
                                temp: tempData == null ? null : tempData,
                            };
                        })
                        .filter((item) => item.temp !== null);

                    // Get the combined data
                    const combinedData = combineUtilitySimulatedData(
                        baseLoadPMF, // apply baseLoadPMF to every temperature bin in the observed temperature range
                        formattedHeatPumpData, // simulated heat pump data
                        true //isUtilityDataPMF
                    );

                    change(`${SESSION_GREEN_BUTTON}.combinedUtilityHeatPumpData`, combinedData);
                }
                */

                // SCALING BASE LOADS
                if (h2kResultsBaseloads > 0) {
                    // has base load results? proceed
                    // new scaled value [kWh/d] = current value [kWh/d] * (Green Button Calculated [GJ/y]) / (HOT2000 calculated [GJ/y])
                    const newElecApplConsumption = parseFloat(
                        ((elecApplConsumption * averageAnnualConsumption_GJ_year) / h2kResultsBaseloads).toFixed(2)
                    );
                    const newLightingConsumption = parseFloat(
                        ((lightingConsumption * averageAnnualConsumption_GJ_year) / h2kResultsBaseloads).toFixed(2)
                    );
                    const newExteriorUseConsumption = parseFloat(
                        ((exteriorUseConsumption * averageAnnualConsumption_GJ_year) / h2kResultsBaseloads).toFixed(2)
                    );
                    const newOtherElecConsumption = parseFloat(
                        ((otherElecConsumption * averageAnnualConsumption_GJ_year) / h2kResultsBaseloads).toFixed(2)
                    );

                    change(`model.modelData.baseLoads.summary.elecApplConsumption`, newElecApplConsumption);
                    change(`model.modelData.baseLoads.summary.lightingConsumption`, newLightingConsumption);
                    change(`model.modelData.baseLoads.summary.exteriorUseConsumption`, newExteriorUseConsumption);
                    change(`model.modelData.baseLoads.summary.otherElecConsumption`, newOtherElecConsumption);

                    change(`${accessor}.baseLoads.summary`, {
                        // saving a copy for restoration purposes in model.modelData.utilityData.baseLoads.summary
                        elecApplConsumption: newElecApplConsumption,
                        lightingConsumption: newLightingConsumption,
                        exteriorUseConsumption: newExteriorUseConsumption,
                        otherElecConsumption: newOtherElecConsumption,
                    });

                    change(`${accessor}.hasBaseLoadCalculation`, true); // base load calculation success
                    await modelSave();
                } else {
                    change(`${accessor}.hasBaseLoadCalculation`, false);
                    console.warn("Base load denominator is zero. Scaling incomplete");
                }
            } else {
                console.warn("Cannot perform base load calculation. Missing Green Button data");
            }
        } catch (error) {
            console.error("Error calculating base load:", error);
        } finally {
            dispatch(toggleGreenButtonBaseLoadCalculationLoading(false));
        }
    }, [
        hourlyGreenButtonData,
        modelSave,
        change,
        accessor,
        dispatch,
        h2kResultsBaseloads,
        h2kResultsVentilation_kWh_day,
        elecApplConsumption,
        lightingConsumption,
        exteriorUseConsumption,
        otherElecConsumption,
    ]);

    const analyzeWithWeatherData = useCallback(async () => {
        dispatch(toggleGreenButtonWeatherAnalysisLoading(true));
        setWeatherAnalysisError("");
        try {
            await modelSave();

            const res = await getGreenButtonWeatherData({ modelId });

            if (res?.isAxiosError) {
                const apiMsg = res.response?.data?.message;
                setWeatherAnalysisError(typeof apiMsg === "string" ? apiMsg : res.message || "Weather analysis failed");
                return;
            }
            if (res?.status === 204) {
                const m = res?.data?.message;
                setWeatherAnalysisError(typeof m === "string" && m ? m : "No Green Button data found for this model.");
                return;
            }
            if (res?.status >= 400) {
                const m = res?.data?.message;
                setWeatherAnalysisError(typeof m === "string" ? m : "Weather analysis failed");
                return;
            }

            const payload = res?.data || {};
            if (payload.empty) {
                setWeatherAnalysisError(
                    typeof payload.message === "string" ? payload.message : "No Green Button data found for this model."
                );
                return;
            }

            const returnedHourlyGreenButtonData = payload.hourlyGreenButtonData ?? [];
            const stationCounts = payload.stationCounts ?? {};
            const hasAnyFiniteTemp = returnedHourlyGreenButtonData.some(
                (row) => row?.temp != null && Number.isFinite(Number(row.temp))
            );

            if (!isEmpty(returnedHourlyGreenButtonData)) {
                /** Same length/order as Firestore: missing weather hours keep `temp: null`; plots filter finite temps. */
                change(`${SESSION_GREEN_BUTTON}.hourlyGreenButtonData`, returnedHourlyGreenButtonData);
            }
            if (!isEmpty(returnedHourlyGreenButtonData) && !hasAnyFiniteTemp) {
                setWeatherAnalysisError(
                    "Weather was retrieved but no hourly temperatures could be aligned with your usage data."
                );
                await modelSave();
                return;
            }
            if (hasAnyFiniteTemp) {
                change(`${accessor}.greenButtonData.hasTemperatureData`, true);
                change(`${accessor}.greenButtonData.stationCounts`, stationCounts);
                setDomainSelection("temperatureDomain");
            }

            /** Weather-only endpoint returns no heat-pump merge; always sync so stale combined bins are cleared. */
            const returnedBinnedUtilityData = payload.combinedUtilityHeatPumpData ?? [];
            const filteredDataset = Array.isArray(returnedBinnedUtilityData)
                ? returnedBinnedUtilityData.filter((row) => row?.combinedBoxplotParams != null)
                : [];
            change(`${SESSION_GREEN_BUTTON}.combinedUtilityHeatPumpData`, filteredDataset);

            mixpanel.track("Green Button Weather", {
                ModelId: modelId,
                HasTemperatureData: hasAnyFiniteTemp,
                HourlyCount: returnedHourlyGreenButtonData.length,
            });

            await modelSave();
        } catch (error) {
            // console.warn("error", error);
            setWeatherAnalysisError(error?.message || "Weather analysis failed");
        } finally {
            dispatch(toggleGreenButtonWeatherAnalysisLoading(false));
        }
    }, [dispatch, modelId, change, accessor, modelSave]);

    const hasGreenButtonContent =
        !isEmpty(hourlyGreenButtonData) || !isEmpty(greenButtonDocumentIds) || !!hourlyDataSourceFileName;

    const handleClearGreenButtonData = useCallback(async () => {
        if (!modelId) return;
        try {
            setClearingGreenButton(true);
            const res = await deleteModelGreenButtonData({ modelId });
            if (res?.isAxiosError || res?.response?.status >= 400) {
                throw new Error(res?.message || "Failed to delete Green Button data");
            }
            dispatch(clearGreenButtonData());
            await modelSave?.();
            setXmlUploadResetKey((k) => k + 1);
        } catch (e) {
            console.warn("Clear Green Button data failed", e);
        } finally {
            setClearingGreenButton(false);
        }
    }, [modelId, dispatch, modelSave]);

    const tableRows = useMemo(() => {
        return rows.map(({ year, month, billingStartDate, fuelUse, fuelCost, notes }, ind) => ({
            // these are the columns and their input
            year: {
                value: year,
                content: TableInput(
                    selectedFuelType,
                    selectedTableType,
                    "year",
                    ind,
                    accessor,
                    firstMonth,
                    firstYear,
                    change
                ),
            },
            month: {
                value: month,
                content: TableInput(
                    selectedFuelType,
                    selectedTableType,
                    "month",
                    ind,
                    accessor,
                    firstMonth,
                    firstYear,
                    change
                ),
            },
            billingStartDate: {
                value: billingStartDate,
                content: TableInput(
                    selectedFuelType,
                    selectedTableType,
                    "billingStartDate",
                    ind,
                    accessor,
                    firstMonth,
                    firstYear,
                    change
                ),
            },
            fuelUse: {
                value: fuelUse,
                content: TableInput(
                    selectedFuelType,
                    selectedTableType,
                    "fuelUse",
                    ind,
                    accessor,
                    firstMonth,
                    firstYear,
                    change
                ),
            },
            fuelCost: {
                value: fuelCost,
                content: TableInput(
                    selectedFuelType,
                    selectedTableType,
                    "fuelCost",
                    ind,
                    accessor,
                    firstMonth,
                    firstYear,
                    change
                ),
            },
            notes: {
                value: notes,
                content: TableInput(
                    selectedFuelType,
                    selectedTableType,
                    "notes",
                    ind,
                    accessor,
                    firstMonth,
                    firstYear,
                    change
                ),
            },
        }));
    }, [selectedFuelType, selectedTableType, accessor, firstMonth, firstYear, change]);

    return (
        <>
            <div style={{ marginLeft: "1rem", marginRight: "1rem", marginTop: "1rem" }}>
                {GREEN_BUTTON_ENABLED && (
                    <>
                        <div className={classes.headerWithIcon}>
                            <h3>Green Button Data Analysis</h3>
                            <button
                                className={classes.helpButton}
                                onClick={() => setIsInfoDialogOpen(true)}
                                aria-label="Green Button Data Information"
                            >
                                <HelpIcon />
                            </button>
                        </div>

                        {/* Step 1: Upload File */}
                        <div className={`${classes.stepContainer} ${step1Open ? classes.stepOpen : ""}`}>
                            <div className={classes.stepHeader} onClick={() => setStep1Open(!step1Open)}>
                                <div className={classes.stepNumber}>1</div>
                                <div className={classes.stepTitle}>Upload Green Button Data File</div>
                                {uploadIsLoading && (
                                    <div className={classes.stepLoading} aria-live="polite">
                                        <LoadingDots small />
                                        <span>Processing…</span>
                                    </div>
                                )}
                                {!isEmpty(hourlyGreenButtonData) && !uploadIsLoading && (
                                    <div className={classes.stepComplete}>
                                        <span className={classes.checkmark}>✓</span>
                                        <span>Complete</span>
                                    </div>
                                )}

                                <div className={classes.stepToggle}>
                                    <ArrowDown />
                                </div>
                            </div>
                            <Collapse in={step1Open} timeout="auto" unmountOnExit>
                                <div className={classes.stepContent}>
                                    <div className={classes.step1Container}>
                                        <XmlUpload
                                            modelId={modelId}
                                            change={change}
                                            modelSave={modelSave}
                                            uploadResetKey={xmlUploadResetKey}
                                            onUploadStart={handleGreenButtonUploadStart}
                                        />
                                        {hasGreenButtonContent && (
                                            <div className={classes.stepHeaderExtras}>
                                                <Button
                                                    type="hollow"
                                                    smallPadding
                                                    className={classes.clearGreenButton}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClearGreenButtonData();
                                                    }}
                                                    disabled={
                                                        !modelId ||
                                                        uploadIsLoading ||
                                                        clearingGreenButton ||
                                                        loadingTemperatureData ||
                                                        loadingBaseLoadCalculation
                                                    }
                                                >
                                                    {clearingGreenButton ? "Clearing…" : "Clear Green Button data"}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    {!isEmpty(hourlyGreenButtonData) && !uploadIsLoading && (
                                        <div className={classes.successMessage}>
                                            Data successfully uploaded and processed. {hourlyGreenButtonData.length}{" "}
                                            hourly records loaded.
                                        </div>
                                    )}
                                </div>
                            </Collapse>
                        </div>

                        {/* Step 2: Demonstrated Load & Hourly Analysis */}
                        <div
                            className={`${classes.stepContainer} ${step2Open ? classes.stepOpen : ""} ${
                                isEmpty(hourlyGreenButtonData) ? classes.stepDisabled : ""
                            }`}
                        >
                            <div
                                className={classes.stepHeader}
                                onClick={() => !isEmpty(hourlyGreenButtonData) && setStep2Open(!step2Open)}
                            >
                                <div className={classes.stepNumber}>2</div>
                                <div className={classes.stepTitle}>Demonstrated Load &amp; Hourly Analysis</div>
                                {!isEmpty(hourlyGreenButtonData) && !uploadIsLoading && (
                                    <div className={classes.stepComplete}>
                                        <span className={classes.checkmark}>✓</span>
                                        <span>Ready</span>
                                    </div>
                                )}
                                <div className={classes.stepToggle}>
                                    <ArrowDown />
                                </div>
                            </div>
                            <Collapse in={step2Open && !isEmpty(hourlyGreenButtonData)} timeout="auto" unmountOnExit>
                                <div className={classes.stepContent}>
                                    <DemonstratedLoadSettingsPanel
                                        demonstratedLoad={demonstratedLoad}
                                        onUpdateSettings={updateDemonstratedLoadSettings}
                                    />

                                    <div className={classes.plotControlsSection}>
                                        <h4>Data Visualization</h4>
                                        <InputRow style={{ alignItems: "flex-end" }} gridTemplate="2fr 2fr 1fr 1fr">
                                            <Select
                                                name={"userPlotSelection"}
                                                label="Plot Type"
                                                options={[{ label: "Scatter Plot", value: "scatterPlot" }]}
                                                disabled={false}
                                                setValue={selectedPlotType}
                                                input={{
                                                    onChange: (value) => {
                                                        setSelectedPlotType(value);
                                                        if (value === "boxPlot" && hasTemperatureData) {
                                                            setDomainSelection("temperatureDomain");
                                                        }
                                                    },
                                                    value: selectedPlotType,
                                                }}
                                            />
                                            {selectedPlotType === "scatterPlot" && hasTemperatureData ? (
                                                <Select
                                                    name={"domainSelection"}
                                                    label={"Domain (X-Axis)"}
                                                    info={"Select the domain for the scatter plot"}
                                                    options={[
                                                        { label: "Temperature", value: "temperatureDomain" },
                                                        { label: "Time", value: "timeDomain" },
                                                    ]}
                                                    disabled={false}
                                                    setValue={selectedDomain}
                                                    input={{
                                                        onChange: (value) => setDomainSelection(value),
                                                        value: selectedDomain,
                                                    }}
                                                />
                                            ) : selectedPlotType === "scatterPlot" ? (
                                                <Select
                                                    name={"domainSelection"}
                                                    label={"Domain (X-Axis)"}
                                                    info={
                                                        "Temperature domain available after weather data analysis (Step 3)"
                                                    }
                                                    options={[{ label: "Time", value: "timeDomain" }]}
                                                    disabled={true}
                                                    setValue={selectedDomain}
                                                    input={{
                                                        onChange: (value) => setDomainSelection(value),
                                                        value: selectedDomain,
                                                    }}
                                                />
                                            ) : null}
                                            <Select
                                                name="demonstratedLoadYAxis"
                                                label="Chart Y-axis"
                                                options={[
                                                    { label: "Energy (kWh)", value: "kwh" },
                                                    { label: "Current (A)", value: "amps" },
                                                ]}
                                                disabled={selectedDomain !== "timeDomain"}
                                                input={{
                                                    value: demonstratedLoadYAxis,
                                                    onChange: setDemonstratedLoadYAxis,
                                                }}
                                            />
                                            <CsvExport
                                                hourlyDataset={hourlyGreenButtonData}
                                                binnedDataset={binnedUtilityData}
                                                fileName={hourlyDataSourceFileName}
                                                hasTemperatureData={hasTemperatureData}
                                                exporting={exporting}
                                                exportingCsv={exportingCsv}
                                                toggleExporting={toggleExporting}
                                                toggleCsvExporting={toggleCsvExporting}
                                                toggleErrorMsg={toggleErrorMsg}
                                            />
                                        </InputRow>
                                        {csvErrorMsg && <p className={classes.errorMsg}>{csvErrorMsg}</p>}
                                    </div>

                                    <div className={classes.plotWrapper}>
                                        <Suspense
                                            fallback={<LoadingSection sectionLoadingName={<h3>Loading Plots...</h3>} />}
                                        >
                                            {uploadIsLoading ? (
                                                <LoadingSection sectionLoadingName={<h3>Processing data...</h3>} />
                                            ) : greenButtonFetchError ? (
                                                <p className={classes.errorMsg} role="alert">
                                                    {greenButtonFetchError}
                                                </p>
                                            ) : isGreenButtonChartHydrating ? (
                                                <LoadingSection sectionLoadingName={<h3>Loading chart data...</h3>} />
                                            ) : (
                                                <LazyLoadedPlots
                                                    key={`gb-plot-${greenButtonDocumentIdsKey}-${hourlyGreenButtonData.length}`}
                                                    userPlotSelection={selectedPlotType}
                                                    userDomainSelection={selectedDomain}
                                                    hasBaseLoadCalculation={hasBaseLoadCalculation}
                                                    scatterPlotData={hourlyGreenButtonData || []}
                                                    boxPlotData={binnedUtilityData || []}
                                                    demonstratedLoadChart={demonstratedLoadChart}
                                                />
                                            )}
                                        </Suspense>
                                    </div>

                                    <DemonstratedLoadResults lastComputed={demonstratedLoadResult} />

                                    <ProposedLoadsSection
                                        proposedLoads={proposedLoads}
                                        onAddProposedLoad={addProposedLoad}
                                        onUpdateProposedLoad={updateProposedLoad}
                                        onRemoveProposedLoad={removeProposedLoad}
                                    />
                                </div>
                            </Collapse>
                        </div>

                        {/* Step 3: Analyze with Weather Data */}
                        <div
                            className={`${classes.stepContainer} ${step3Open ? classes.stepOpen : ""} ${
                                isEmpty(hourlyGreenButtonData) ? classes.stepDisabled : ""
                            }`}
                        >
                            <div
                                className={classes.stepHeader}
                                onClick={() => !isEmpty(hourlyGreenButtonData) && setStep3Open(!step3Open)}
                            >
                                <div className={classes.stepNumber}>3</div>
                                <div className={classes.stepTitle}>Analyze with Weather Data</div>
                                {loadingTemperatureData && (
                                    <div className={classes.stepLoading} aria-live="polite">
                                        <LoadingDots small />
                                        <span>Analyzing weather…</span>
                                    </div>
                                )}
                                {hasTemperatureData && !loadingTemperatureData && (
                                    <div className={classes.stepComplete}>
                                        <span className={classes.checkmark}>✓</span>
                                        <span>Complete</span>
                                    </div>
                                )}
                                <div className={classes.stepToggle}>
                                    <ArrowDown />
                                </div>
                            </div>
                            <Collapse in={step3Open && !isEmpty(hourlyGreenButtonData)} timeout="auto" unmountOnExit>
                                <div className={classes.stepContent}>
                                    <p className={classes.stepDescription}>
                                        Combine your utility data with historical weather data to analyze
                                        temperature-dependent consumption patterns.
                                    </p>
                                    <p className={classes.stepDescription}>
                                        This process involves searching historical weather data and across a number of
                                        weather stations and may take up to 1 minute.
                                    </p>
                                    <p className={classes.stepDescription} style={{ marginTop: "0.5rem" }}>
                                        {f280WeatherSearchDisplay.line}
                                    </p>
                                    <InputRow style={{ alignItems: "flex-end" }} gridTemplate="1fr 1fr">
                                        <div>
                                            <Button
                                                size="medium"
                                                variant="contained"
                                                onClick={analyzeWithWeatherData}
                                                color="primary"
                                                style={{ cursor: "pointer", fontSize: 14, marginTop: "1.5rem" }}
                                                disabled={
                                                    isEmpty(hourlyGreenButtonData) ||
                                                    hasTemperatureData ||
                                                    loadingTemperatureData
                                                }
                                            >
                                                {loadingTemperatureData
                                                    ? "Analyzing…"
                                                    : hasTemperatureData
                                                      ? "Weather Data Analyzed"
                                                      : "Analyze with Weather Data"}
                                            </Button>
                                        </div>
                                    </InputRow>
                                    {weatherAnalysisError && (
                                        <p className={classes.errorMsg} role="alert">
                                            {weatherAnalysisError}
                                        </p>
                                    )}
                                    {hasTemperatureData && !loadingTemperatureData && (
                                        <div className={classes.successMessage}>
                                            Weather data successfully analyzed. Temperature-dependent visualizations are
                                            now available.
                                        </div>
                                    )}
                                    {hasTemperatureData && !loadingTemperatureData && !isEmpty(stationCounts) && (
                                        <div className={classes.stepDescription} style={{ marginTop: "1rem" }}>
                                            <strong>Temperature records by weather station</strong>
                                            <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
                                                {Object.keys(stationCounts)
                                                    .sort((a, b) => a.localeCompare(b))
                                                    .map((stationNameKey) => (
                                                        <li key={stationNameKey}>
                                                            <strong>{stationNameKey}</strong>:{" "}
                                                            {Number(stationCounts[stationNameKey]?.count) || 0}{" "}
                                                            temperature records (
                                                            {Math.round(
                                                                100 * Number(stationCounts[stationNameKey]?.fraction)
                                                            )}
                                                            %)
                                                        </li>
                                                    ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </Collapse>
                        </div>

                        <div className={classes.divider}></div>
                    </>
                )}
                {/* <h3 className={classes.headerTitle}>Monthy Utility Bill Data</h3>
                <div className={classes.header}>
                    <Select
                        className={`${globalStyles.typeSelect} ${classes.upgradeSelect}`}
                        name={"fuelType"}
                        label="Fuel Type"
                        options={[
                            {
                                label: "Electricity",
                                value: "electricity",
                            },
                            {
                                label: "Natural Gas",
                                value: "naturalGas",
                            },
                            {
                                label: "Oil",
                                value: "oil",
                            },
                            {
                                label: "Propane",
                                value: "propane",
                            },
                            {
                                label: "Wood (Pellets)",
                                value: "woodPellets",
                            },
                            {
                                label: "Wood (Cord)",
                                value: "woodCord",
                            },
                        ]}
                        disabled={false}
                        setValue={selectedFuelType}
                        input={{
                            onChange: (value) => {
                                setSelectedFuelType(value);
                            },
                            value: selectedFuelType,
                        }}
                    />
                    <Select
                        className={`${globalStyles.typeSelect} ${classes.upgradeSelect} ${classes.upgradeSelectMiddle}`}
                        name={"tableType"}
                        label="Pre or Post Upgrade"
                        options={[
                            {
                                label: "Pre-Upgrade",
                                value: "preUpgrade",
                            },
                            {
                                label: "Post-Upgrade",
                                value: "postUpgrade",
                            },
                        ]}
                        disabled={false}
                        setValue={selectedTableType}
                        input={{
                            onChange: (value) => {
                                setSelectedTableType(value);
                            },
                            value: selectedTableType,
                        }}
                    />
                </div> */}
                {/* <Table
                    // key={tableInd}
                    className={classes.summaryTable}
                    headCells={[
                        {
                            id: "year",
                            label: "Year",
                            align: "left",
                            width: "130",
                        },
                        {
                            id: "month",
                            label: "Month",
                            align: "left",
                            width: "130",
                        },
                        {
                            id: "billingStartDate",
                            label: "Billing Start Date",
                            align: "left",
                            width: "130",
                        },
                        {
                            id: "fuelUse",
                            label: `Total ${fuelTypeLabel} Use (${fuelTypeUnit})`,
                            align: "left",
                            width: "240",
                        },
                        {
                            id: "fuelCost",
                            label: `Total ${fuelTypeLabel} Cost ($)`,
                            align: "left",
                            width: "240",
                        },
                        {
                            id: "notes",
                            label: "Notes",
                            align: "left",
                            // width: "auto",
                            width: "240",
                        },
                    ]}
                    rows={rows.map(({ year, month, billingStartDate, fuelUse, fuelCost, notes }, ind) => ({
                        // these are the columns and their input
                        year: {
                            value: year,
                            content: TableInput(
                                selectedFuelType,
                                selectedTableType,
                                "year",
                                ind,
                                accessor,
                                firstMonth,
                                firstYear,
                                change
                            ),
                        },
                        month: {
                            value: month,
                            content: TableInput(
                                selectedFuelType,
                                selectedTableType,
                                "month",
                                ind,
                                accessor,
                                firstMonth,
                                firstYear,
                                change
                            ),
                        },
                        billingStartDate: {
                            value: billingStartDate,
                            content: TableInput(
                                selectedFuelType,
                                selectedTableType,
                                "billingStartDate",
                                ind,
                                accessor,
                                firstMonth,
                                firstYear,
                                change
                            ),
                        },
                        fuelUse: {
                            value: fuelUse,
                            content: TableInput(
                                selectedFuelType,
                                selectedTableType,
                                "fuelUse",
                                ind,
                                accessor,
                                firstMonth,
                                firstYear,
                                change
                            ),
                        },
                        fuelCost: {
                            value: fuelCost,
                            content: TableInput(
                                selectedFuelType,
                                selectedTableType,
                                "fuelCost",
                                ind,
                                accessor,
                                firstMonth,
                                firstYear,
                                change
                            ),
                        },
                        notes: {
                            value: notes,
                            content: TableInput(
                                selectedFuelType,
                                selectedTableType,
                                "notes",
                                ind,
                                accessor,
                                firstMonth,
                                firstYear,
                                change
                            ),
                        },
                    }))}
                /> */}
            </div>
            <Dialog open={isInfoDialogOpen} closeActions={() => setIsInfoDialogOpen(false)}>
                <div className={classes.infoDialog}>
                    <CloseDialog closeActions={() => setIsInfoDialogOpen(false)} />
                    <h2>Green Button Data Analysis</h2>
                    <div className={classes.dialogContent}>
                        <p>
                            Using Green Button data file uploads (.xml), you can investigate the real-world hourly
                            utility data of a building to better understand its consumption patterns.
                        </p>
                        <p>
                            You or your client can request a Green Button data file from your utility provider. You can
                            find more information about Green Button data{" "}
                            <a
                                href="https://www.greenbuttonalliance.org/canadian-initiatives"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                here
                            </a>
                            .
                        </p>
                        <p>
                            Volta SNAP can use Green Button data to help you analyze your building's electricity use in
                            a number of ways:
                        </p>
                        <ul>
                            <li>View and download the raw hourly data in csv format.</li>
                            <li>
                                Calculate demonstrated peak electrical demand from hourly data (ESA Bulletin 8-3-16).
                            </li>
                            <li>
                                Estimate the base electrical load of your building and calibrate your model's General
                                Mode and Household Operating Condition results to better reflect real-world usage.
                            </li>
                            <li>Analyze the temperature dependence of your building's electricity use.</li>
                            {/* <li>
                                Combine real-world data with simulated heat pump data to better understand electrical
                                load impacts of heat pump upgrades.
                            </li> */}
                        </ul>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const mapDispatchToProps = (dispatch) => ({
    changeUtilityDataPackage: (pkg) => dispatch(changeUtilityDataPackage(pkg)),
});

export default connect(null, mapDispatchToProps)(UtilityData);
